"use client"

import { useState, useEffect, useRef, useMemo, useSyncExternalStore } from "react"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card } from "@/components/ui/card"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Trash2,
  Download,
  ImageIcon,
  X,
  FilePlus2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Copy,
  History,
  Bookmark,
  Plus,
  Settings2,
  Printer,
  MoreHorizontal,
  FileJson,
  FileSpreadsheet,
  Upload,
  Sparkles,
} from "lucide-react"
import { toast } from "sonner"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import { pdf } from "@react-pdf/renderer"
import { format, addDays, parseISO, isValid } from "date-fns"
import {
  currencies,
  layouts,
  colorSchemes,
  documentTypes,
  statuses,
  itemUnits,
  paymentTermsPresets,
  defaultInvoice,
  STORAGE_KEY,
  HISTORY_KEY,
  PRESETS_KEY,
  HISTORY_MAX,
  calculateTotals,
  calculateLineNet,
  formatMoneyLocale,
  exampleInvoice,
  nextInvoiceNumber,
  daysOverdue,
  computeLateFee,
  itemsToCsv,
  type InvoiceData,
  type LayoutId,
  type ColorSchemeId,
  type LineItem,
  type DocumentType,
  type InvoiceStatus,
  type DiscountType,
  type ItemUnit,
  type TaxLine,
  type SavedItem,
  type SavedInvoiceMeta,
} from "./invoice-generator-constants"
import { InlineInput, InlineTextarea } from "./invoice-inline-inputs"
import { InvoicePdfDocument } from "./invoice-pdf-document"

const subscribeToHydration = () => () => {}

function readStoredInvoice(): InvoiceData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? { ...defaultInvoice, ...JSON.parse(stored) } : defaultInvoice
  } catch {
    return defaultInvoice
  }
}

function readStoredItems<T>(key: string): T[] {
  try {
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export function InvoiceGenerator() {
  const isHydrated = useSyncExternalStore(subscribeToHydration, () => true, () => false)

  if (!isHydrated) {
    return <div className="h-[600px] animate-pulse bg-muted rounded-xl" />
  }

  return <InvoiceGeneratorContent />
}

function InvoiceGeneratorContent() {
  const [invoice, setInvoice] = useState<InvoiceData>(readStoredInvoice)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [history, setHistory] = useState<Array<{ meta: SavedInvoiceMeta; data: InvoiceData }>>(
    () => readStoredItems(HISTORY_KEY)
  )
  const [presets, setPresets] = useState<SavedItem[]>(() => readStoredItems(PRESETS_KEY))
  const printRef = useRef<HTMLDivElement>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(invoice))
  }, [invoice])

  const updateField = <K extends keyof InvoiceData>(field: K, value: InvoiceData[K]) => {
    setInvoice((prev) => ({ ...prev, [field]: value }))
  }

  const updateIssueDate = (newIssue: string) => {
    setInvoice((prev) => {
      const preset = paymentTermsPresets.find((p) => p.label === prev.paymentTerms)
      let dueDate = prev.dueDate
      if (preset && preset.days !== null && isValid(parseISO(newIssue))) {
        dueDate = format(addDays(parseISO(newIssue), preset.days), "yyyy-MM-dd")
      }
      return { ...prev, issueDate: newIssue, dueDate }
    })
  }

  const updatePaymentTerms = (terms: string) => {
    setInvoice((prev) => {
      const preset = paymentTermsPresets.find((p) => p.label === terms)
      let dueDate = prev.dueDate
      if (preset && preset.days !== null && isValid(parseISO(prev.issueDate))) {
        dueDate = format(addDays(parseISO(prev.issueDate), preset.days), "yyyy-MM-dd")
      }
      return { ...prev, paymentTerms: terms, dueDate }
    })
  }

  const addItem = (preset?: SavedItem) => {
    setInvoice((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: crypto.randomUUID(),
          description: preset?.description ?? "",
          quantity: 1,
          rate: preset?.rate ?? 0,
          unit: preset?.unit ?? prev.defaultItemUnit,
        },
      ],
    }))
  }

  const duplicateItem = (id: string) => {
    setInvoice((prev) => {
      const idx = prev.items.findIndex((i) => i.id === id)
      if (idx < 0) return prev
      const original = prev.items[idx]
      const copy: LineItem = { ...original, id: crypto.randomUUID() }
      const items = [...prev.items]
      items.splice(idx + 1, 0, copy)
      return { ...prev, items }
    })
  }

  const removeItem = (id: string) => {
    if (invoice.items.length === 1) return
    setInvoice((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }))
  }

  const moveItem = (id: string, direction: -1 | 1) => {
    setInvoice((prev) => {
      const idx = prev.items.findIndex((i) => i.id === id)
      const target = idx + direction
      if (idx < 0 || target < 0 || target >= prev.items.length) return prev
      const items = [...prev.items]
      ;[items[idx], items[target]] = [items[target], items[idx]]
      return { ...prev, items }
    })
  }

  const updateItem = <K extends keyof LineItem>(id: string, field: K, value: LineItem[K]) => {
    setInvoice((prev) => ({
      ...prev,
      items: prev.items.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    }))
  }

  const savePresetFromItem = (item: LineItem) => {
    if (!item.description.trim()) {
      toast.error("Add a description before saving")
      return
    }
    const next: SavedItem = {
      id: crypto.randomUUID(),
      description: item.description,
      rate: item.rate,
      unit: item.unit || "units",
    }
    const dedup = presets.filter(
      (p) => p.description.toLowerCase() !== next.description.toLowerCase()
    )
    const updated = [next, ...dedup].slice(0, 50)
    setPresets(updated)
    localStorage.setItem(PRESETS_KEY, JSON.stringify(updated))
    toast.success(`Saved "${next.description}" to presets`)
  }

  const removePreset = (id: string) => {
    const updated = presets.filter((p) => p.id !== id)
    setPresets(updated)
    localStorage.setItem(PRESETS_KEY, JSON.stringify(updated))
  }

  const addTaxLine = () => {
    setInvoice((prev) => ({
      ...prev,
      taxes: [
        ...prev.taxes,
        {
          id: crypto.randomUUID(),
          name: prev.taxes.length === 0 ? "VAT" : "Tax",
          rate: 0,
        },
      ],
    }))
  }

  const updateTaxLine = (id: string, patch: Partial<TaxLine>) => {
    setInvoice((prev) => ({
      ...prev,
      taxes: prev.taxes.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    }))
  }

  const removeTaxLine = (id: string) => {
    setInvoice((prev) => ({ ...prev, taxes: prev.taxes.filter((t) => t.id !== id) }))
  }

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 1024 * 1024) {
      toast.error("Logo must be less than 1MB")
      return
    }
    const reader = new FileReader()
    reader.onload = () => updateField("logo", reader.result as string)
    reader.readAsDataURL(file)
  }

  const saveToHistory = () => {
    const meta: SavedInvoiceMeta = {
      id: invoice.invoiceNumber,
      invoiceNumber: invoice.invoiceNumber,
      billTo: invoice.billTo.split("\n")[0] || "Unknown",
      total: totals.total,
      currency: invoice.currency,
      issueDate: invoice.issueDate,
      status: invoice.status,
      documentType: invoice.documentType,
      updatedAt: new Date().toISOString(),
    }
    const filtered = history.filter((h) => h.meta.id !== meta.id)
    const updated = [{ meta, data: invoice }, ...filtered].slice(0, HISTORY_MAX)
    setHistory(updated)
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated))
  }

  const loadFromHistory = (data: InvoiceData) => {
    setInvoice(data)
    toast.success(`Loaded ${data.invoiceNumber}`)
  }

  const deleteFromHistory = (id: string) => {
    const updated = history.filter((h) => h.meta.id !== id)
    setHistory(updated)
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated))
  }

  const resetInvoice = () => {
    saveToHistory()
    const keepFrom = invoice.from
    const keepPaymentInfo = invoice.paymentInfo
    setInvoice({
      ...defaultInvoice,
      from: keepFrom,
      paymentInfo: keepPaymentInfo,
      logo: invoice.logo,
      currency: invoice.currency,
      layout: invoice.layout,
      colorScheme: invoice.colorScheme,
      documentType: invoice.documentType,
      taxes: invoice.taxes.map((t) => ({ ...t, id: crypto.randomUUID() })),
      defaultItemUnit: invoice.defaultItemUnit,
      invoiceNumber: nextInvoiceNumber(invoice.invoiceNumber),
    })
    toast.success("Saved & ready for new")
  }

  const duplicateInvoice = () => {
    setInvoice((prev) => ({
      ...prev,
      invoiceNumber: nextInvoiceNumber(prev.invoiceNumber),
      issueDate: format(new Date(), "yyyy-MM-dd"),
      status: "none",
      amountPaid: 0,
    }))
    toast.success("Duplicated as new invoice")
  }

  const loadExample = () => {
    setInvoice({
      ...defaultInvoice,
      ...exampleInvoice,
      items: (exampleInvoice.items || []).map((i) => ({ ...i, id: crypto.randomUUID() })),
      taxes: (exampleInvoice.taxes || []).map((t) => ({ ...t, id: crypto.randomUUID() })),
      logo: invoice.logo,
      issueDate: format(new Date(), "yyyy-MM-dd"),
      dueDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
    } as InvoiceData)
    toast.success("Sample invoice loaded")
  }

  const printInvoice = () => {
    if (typeof window === "undefined") return
    window.print()
  }

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(invoice, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${invoice.invoiceNumber || "invoice"}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    toast.success("Invoice data exported")
  }

  const exportCsv = () => {
    if (invoice.items.length === 0) {
      toast.error("No items to export")
      return
    }
    const csv = itemsToCsv(invoice)
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${invoice.invoiceNumber || "invoice"}-items.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    toast.success("CSV exported")
  }

  const jsonImportRef = useRef<HTMLInputElement>(null)

  const importJson = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string) as Partial<InvoiceData>
        if (!parsed.items || !Array.isArray(parsed.items)) {
          toast.error("Invalid invoice file — missing items")
          return
        }
        setInvoice({ ...defaultInvoice, ...parsed } as InvoiceData)
        toast.success("Invoice imported")
      } catch {
        toast.error("Could not parse JSON file")
      }
    }
    reader.readAsText(file)
    e.target.value = ""
  }

  const currency = currencies.find((c) => c.code === invoice.currency) || currencies[0]
  const totals = calculateTotals(invoice)
  const {
    subtotal,
    itemDiscountTotal,
    itemTaxTotal,
    globalDiscountAmount,
    globalTaxAmount,
    shippingAmount,
    total,
    balanceDue,
  } = totals

  const overdueDays = useMemo(() => {
    if (invoice.status === "paid" || invoice.documentType === "receipt" || invoice.documentType === "quote") return 0
    return daysOverdue(invoice.dueDate)
  }, [invoice.dueDate, invoice.status, invoice.documentType])

  const lateFee = computeLateFee(balanceDue, invoice.lateFeeRate, overdueDays)

  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const paymentLink = invoice.paymentLink?.trim() ?? ""
  useEffect(() => {
    let cancelled = false
    if (!paymentLink) return

    import("qrcode")
      .then((mod) => mod.default.toDataURL(paymentLink, { errorCorrectionLevel: "M", margin: 0, width: 200 }))
      .then((url) => {
        if (!cancelled) setQrDataUrl(url)
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [paymentLink])

  const visibleQrDataUrl = paymentLink ? qrDataUrl : null

  const addLateFeeAsLineItem = () => {
    if (lateFee <= 0) return
    setInvoice((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: crypto.randomUUID(),
          description: `Late fee (${overdueDays} days overdue at ${invoice.lateFeeRate}%/mo)`,
          quantity: 1,
          rate: lateFee,
          unit: prev.defaultItemUnit,
        },
      ],
    }))
    toast.success("Late fee added as a line item")
  }

  const formatMoney = (amount: number) => formatMoneyLocale(amount, invoice.currency)

  const colors = colorSchemes.find((c) => c.id === invoice.colorScheme) || colorSchemes[0]
  const docType = documentTypes.find((d) => d.id === invoice.documentType) || documentTypes[0]
  const statusConfig = statuses.find((s) => s.id === invoice.status) || statuses[0]

  const downloadPDF = async () => {
    if (isGeneratingPDF) return
    setIsGeneratingPDF(true)
    saveToHistory()

    try {
      const blob = await pdf(
        <InvoicePdfDocument
          invoice={invoice}
          colors={colors}
          formatMoney={formatMoney}
          subtotal={subtotal}
          taxAmount={globalTaxAmount + itemTaxTotal}
          discountAmount={globalDiscountAmount + itemDiscountTotal}
          shippingAmount={shippingAmount}
          total={total}
          balanceDue={balanceDue}
          docTitle={docType.title}
        />
      ).toBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `${docType.label} ${invoice.invoiceNumber}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      toast.success("PDF downloaded!")
    } catch (error) {
      console.error("PDF generation failed:", error)
      toast.error("Failed to generate PDF")
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const renderStatusBadge = () => {
    if (invoice.status === "none") return null
    return (
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: statusConfig.color, background: statusConfig.bg }}
      >
        {statusConfig.label}
      </span>
    )
  }

  const renderHeader = () => {
    if (invoice.layout === "modern") {
      return (
        <div className="text-center mb-8">
          {invoice.logo && (
            <div className="relative inline-block group mb-4">
              <Image src={invoice.logo} alt="Company logo" width={180} height={80} unoptimized className="max-w-[180px] max-h-[80px] object-contain mx-auto" />
              <button
                onClick={() => updateField("logo", null)}
                className="print-hidden absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all shadow-md hover:bg-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
          {!invoice.logo && (
            <button
              onClick={() => logoInputRef.current?.click()}
              className="print-hidden mx-auto mb-4 w-[160px] h-[70px] border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-1.5 text-muted-foreground/70 hover:border-input hover:text-muted-foreground hover:bg-muted/40 transition-all"
            >
              <ImageIcon className="h-6 w-6" />
              <span className="text-xs font-medium">Add Logo</span>
            </button>
          )}
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ color: colors.accent }}>
            {docType.title}
          </h1>
          <div className="flex justify-center items-center gap-2 mb-3">{renderStatusBadge()}</div>
          <div className="flex justify-center gap-8 text-sm text-foreground/70">
            <div>
              <span className="text-muted-foreground/70"># </span>
              <InlineInput
                value={invoice.invoiceNumber}
                onChange={(v) => updateField("invoiceNumber", v)}
                placeholder="INV-0001"
                className="font-medium w-24"
              />
            </div>
            <div>
              <span className="text-muted-foreground/70">Date: </span>
              <InlineInput
                value={invoice.issueDate}
                onChange={updateIssueDate}
                placeholder=""
                type="date"
                className="w-32"
              />
            </div>
            <div>
              <span className="text-muted-foreground/70">Due: </span>
              <InlineInput
                value={invoice.dueDate}
                onChange={(v) => updateField("dueDate", v)}
                placeholder=""
                type="date"
                className="w-32"
              />
            </div>
          </div>
        </div>
      )
    }

    if (invoice.layout === "compact") {
      return (
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            {invoice.logo ? (
              <div className="relative group">
                <Image src={invoice.logo} alt="Company logo" width={120} height={50} unoptimized className="max-w-[120px] max-h-[50px] object-contain" />
                <button
                  onClick={() => updateField("logo", null)}
                  className="print-hidden absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all shadow-md"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => logoInputRef.current?.click()}
                className="print-hidden w-[100px] h-[45px] border-2 border-dashed border-border rounded flex items-center justify-center text-muted-foreground/70 hover:border-input text-xs"
              >
                <ImageIcon className="h-4 w-4" />
              </button>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold" style={{ color: colors.accent }}>
                  {docType.title}
                </h1>
                {renderStatusBadge()}
              </div>
              <div className="text-sm text-muted-foreground">
                <InlineInput
                  value={invoice.invoiceNumber}
                  onChange={(v) => updateField("invoiceNumber", v)}
                  placeholder="INV-0001"
                  className="font-medium w-24"
                />
              </div>
            </div>
          </div>
          <div
            className="text-right py-3 px-5 rounded-lg"
            style={{ background: `${colors.accent}10`, borderLeft: `4px solid ${colors.accent}` }}
          >
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              {invoice.documentType === "receipt" ? "Amount Paid" : "Balance Due"}
            </div>
            <div className="text-2xl font-bold" style={{ color: colors.accent }}>
              {formatMoney(invoice.documentType === "receipt" ? total : balanceDue)}
            </div>
          </div>
        </div>
      )
    }

    if (invoice.layout === "sidebar") {
      return null
    }

    if (invoice.layout === "split") {
      return (
        <div className="mb-8">
          <div className="flex justify-between items-center pb-6 mb-6 border-b-2" style={{ borderColor: colors.accent }}>
            {invoice.logo ? (
              <div className="relative group">
                <Image src={invoice.logo} alt="Company logo" width={180} height={80} unoptimized className="max-w-[180px] max-h-[80px] object-contain" />
                <button
                  onClick={() => updateField("logo", null)}
                  className="print-hidden absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all shadow-md"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => logoInputRef.current?.click()}
                className="print-hidden w-[160px] h-[70px] border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-1.5 text-muted-foreground/70 hover:border-input"
              >
                <ImageIcon className="h-6 w-6" />
                <span className="text-xs font-medium">Add Logo</span>
              </button>
            )}
            <div className="text-right">
              <h1 className="text-4xl font-bold tracking-tight" style={{ color: colors.accent }}>
                {docType.title}
              </h1>
              <div className="mt-2 flex justify-end">{renderStatusBadge()}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-1 text-sm">
              <div className="flex gap-4"><span className="text-muted-foreground/70 w-20">Number:</span><InlineInput value={invoice.invoiceNumber} onChange={(v) => updateField("invoiceNumber", v)} placeholder="INV-0001" className="font-medium" /></div>
              <div className="flex gap-4"><span className="text-muted-foreground/70 w-20">Date:</span><InlineInput value={invoice.issueDate} onChange={updateIssueDate} placeholder="" type="date" /></div>
              <div className="flex gap-4"><span className="text-muted-foreground/70 w-20">Terms:</span><InlineInput value={invoice.paymentTerms} onChange={updatePaymentTerms} placeholder="Net 30" /></div>
              <div className="flex gap-4"><span className="text-muted-foreground/70 w-20">Due:</span><InlineInput value={invoice.dueDate} onChange={(v) => updateField("dueDate", v)} placeholder="" type="date" /></div>
              {invoice.poNumber && <div className="flex gap-4"><span className="text-muted-foreground/70 w-20">PO #:</span><InlineInput value={invoice.poNumber} onChange={(v) => updateField("poNumber", v)} placeholder="" /></div>}
            </div>
            <div className="text-right">
              <div className="inline-block text-left py-3 px-5 rounded-lg" style={{ background: `${colors.accent}10` }}>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  {invoice.documentType === "receipt" ? "Amount Paid" : "Amount Due"}
                </div>
                <div className="text-2xl font-bold" style={{ color: colors.accent }}>
                  {formatMoney(invoice.documentType === "receipt" ? total : balanceDue)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="header flex flex-col sm:flex-row justify-between items-start gap-4 mb-8">
        <div className="logo">
          {invoice.logo ? (
            <div className="relative group">
              <Image src={invoice.logo} alt="Company logo" width={180} height={80} unoptimized className="max-w-[180px] max-h-[80px] object-contain" />
              <button
                onClick={() => updateField("logo", null)}
                className="print-hidden absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all shadow-md hover:bg-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => logoInputRef.current?.click()}
              className="print-hidden w-[160px] h-[70px] border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-1.5 text-muted-foreground/70 hover:border-input hover:text-muted-foreground hover:bg-muted/40 transition-all"
            >
              <ImageIcon className="h-6 w-6" />
              <span className="text-xs font-medium">Add Logo</span>
            </button>
          )}
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-2 mb-2">
            {renderStatusBadge()}
            <h1 className="invoice-title text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: colors.accent }}>
              {docType.title}
            </h1>
          </div>
          <div className="meta-grid grid grid-cols-[auto_auto] gap-x-4 gap-y-1.5 text-sm">
            <span className="text-muted-foreground text-right">#</span>
            <InlineInput value={invoice.invoiceNumber} onChange={(v) => updateField("invoiceNumber", v)} placeholder="INV-0001" className="w-28 text-right font-medium" />
            <span className="text-muted-foreground text-right">Date</span>
            <InlineInput value={invoice.issueDate} onChange={updateIssueDate} placeholder="" type="date" className="w-32 text-right" />
            <span className="text-muted-foreground text-right">Terms</span>
            <PaymentTermsField value={invoice.paymentTerms} onChange={updatePaymentTerms} />
            <span className="text-muted-foreground text-right">Due</span>
            <InlineInput value={invoice.dueDate} onChange={(v) => updateField("dueDate", v)} placeholder="" type="date" className="w-32 text-right" />
            {invoice.poNumber && (
              <>
                <span className="text-muted-foreground text-right">PO #</span>
                <InlineInput value={invoice.poNumber} onChange={(v) => updateField("poNumber", v)} placeholder="" className="w-28 text-right" />
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderPartyBlock = (
    label: string,
    addressValue: string,
    onAddressChange: (v: string) => void,
    taxIdValue: string,
    onTaxIdChange: (v: string) => void,
    placeholder: string,
    options: { center?: boolean; bg?: string; padding?: string } = {}
  ) => (
    <div className={cn(options.padding, options.bg && "rounded-lg", options.center && "text-center")} style={{ background: options.bg }}>
      <h4 className={cn("text-[10px] font-semibold text-muted-foreground uppercase tracking-wider", options.center ? "mb-3" : "mb-1.5")}>{label}</h4>
      <InlineTextarea
        value={addressValue}
        onChange={onAddressChange}
        placeholder={placeholder}
        className={cn("text-sm", options.center && "text-center", options.bg && "bg-transparent")}
        minRows={3}
      />
      {(taxIdValue || addressValue) && (
        <div className={cn("mt-1.5 flex items-center gap-1.5", options.center && "justify-center")}>
          <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider">Tax ID</span>
          <InlineInput
            value={taxIdValue}
            onChange={onTaxIdChange}
            placeholder="VAT / EIN / GSTIN"
            className={cn("text-xs", options.center && "text-center", options.bg && "bg-transparent")}
          />
        </div>
      )}
    </div>
  )

  const renderParties = () => {
    if (invoice.layout === "split") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-8">
          {renderPartyBlock(
            "From",
            invoice.from,
            (v) => updateField("from", v),
            invoice.fromTaxId,
            (v) => updateField("fromTaxId", v),
            "Your Name / Company\nAddress\nEmail / Phone",
            { bg: "#f9fafb", padding: "p-4" }
          )}
          {renderPartyBlock(
            "Bill To",
            invoice.billTo,
            (v) => updateField("billTo", v),
            invoice.billToTaxId,
            (v) => updateField("billToTaxId", v),
            "Client Name / Company\nAddress\nEmail",
            { bg: "#f9fafb", padding: "p-4" }
          )}
        </div>
      )
    }

    if (invoice.layout === "compact") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 text-sm">
          {renderPartyBlock(
            "From",
            invoice.from,
            (v) => updateField("from", v),
            invoice.fromTaxId,
            (v) => updateField("fromTaxId", v),
            "Your info"
          )}
          {renderPartyBlock(
            "Bill To",
            invoice.billTo,
            (v) => updateField("billTo", v),
            invoice.billToTaxId,
            (v) => updateField("billToTaxId", v),
            "Client info"
          )}
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground/70">Date:</span><InlineInput value={invoice.issueDate} onChange={updateIssueDate} type="date" placeholder="" className="w-28 text-right" /></div>
            <div className="flex justify-between"><span className="text-muted-foreground/70">Due:</span><InlineInput value={invoice.dueDate} onChange={(v) => updateField("dueDate", v)} type="date" placeholder="" className="w-28 text-right" /></div>
            <div className="flex justify-between items-center"><span className="text-muted-foreground/70">Terms:</span><PaymentTermsField value={invoice.paymentTerms} onChange={updatePaymentTerms} width="w-28" /></div>
            {invoice.poNumber && <div className="flex justify-between"><span className="text-muted-foreground/70">PO #:</span><InlineInput value={invoice.poNumber} onChange={(v) => updateField("poNumber", v)} placeholder="" className="w-28 text-right" /></div>}
          </div>
        </div>
      )
    }

    if (invoice.layout === "modern") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 mb-8">
          <div className="text-center p-6 border border-border/60 rounded-xl">
            {renderPartyBlock(
              "From",
              invoice.from,
              (v) => updateField("from", v),
              invoice.fromTaxId,
              (v) => updateField("fromTaxId", v),
              "Your Name / Company\nAddress\nEmail / Phone",
              { center: true }
            )}
          </div>
          <div className="text-center p-6 border border-border/60 rounded-xl">
            {renderPartyBlock(
              "Bill To",
              invoice.billTo,
              (v) => updateField("billTo", v),
              invoice.billToTaxId,
              (v) => updateField("billToTaxId", v),
              "Client Name / Company\nAddress\nEmail",
              { center: true }
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="parties grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
        {renderPartyBlock(
          "From",
          invoice.from,
          (v) => updateField("from", v),
          invoice.fromTaxId,
          (v) => updateField("fromTaxId", v),
          "Your Name / Company\nAddress\nEmail / Phone"
        )}
        {renderPartyBlock(
          "Bill To",
          invoice.billTo,
          (v) => updateField("billTo", v),
          invoice.billToTaxId,
          (v) => updateField("billToTaxId", v),
          "Client Name / Company\nAddress\nEmail"
        )}
        <div className="party hidden lg:block">
          <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Ship To</h4>
          <InlineTextarea value={invoice.shipTo} onChange={(v) => updateField("shipTo", v)} placeholder="(optional)" className="text-sm" minRows={3} />
        </div>
      </div>
    )
  }

  const renderItemRow = (item: LineItem, idx: number) => {
    const unitMeta = itemUnits.find((u) => u.id === (item.unit || "units"))
    const lineNet = calculateLineNet(item)
    const hasItemAdjustment = !!(item.discount || item.taxRate)
    const isLast = idx === invoice.items.length - 1
    const isFirst = idx === 0
    const isHourly = item.unit === "hours" || item.unit === "days" || item.unit === "kg"

    return (
      <motion.tr
        key={item.id}
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="group border-b border-border/60 align-top"
      >
        <td className="py-3 px-2">
          <div className="flex items-start gap-2">
            <div className="print-hidden opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-0.5 pt-1">
              <button
                onClick={() => moveItem(item.id, -1)}
                disabled={isFirst}
                className="text-muted-foreground/60 hover:text-muted-foreground disabled:opacity-30"
                title="Move up"
              >
                <ChevronUp className="h-3 w-3" />
              </button>
              <button
                onClick={() => moveItem(item.id, 1)}
                disabled={isLast}
                className="text-muted-foreground/60 hover:text-muted-foreground disabled:opacity-30"
                title="Move down"
              >
                <ChevronDown className="h-3 w-3" />
              </button>
            </div>
            <button
              onClick={() => removeItem(item.id)}
              className={cn(
                "print-hidden text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 pt-1.5 hover:text-red-600",
                invoice.items.length === 1 && "invisible"
              )}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <div className="flex-1 min-w-0">
              <InlineInput
                value={item.description}
                onChange={(v) => updateItem(item.id, "description", v)}
                placeholder="Item description"
                className="w-full text-sm"
              />
              {(item.details !== undefined && item.details !== null) && (
                <InlineTextarea
                  value={item.details}
                  onChange={(v) => updateItem(item.id, "details", v)}
                  placeholder="Add details, bullet points, sub-tasks..."
                  className="text-xs text-muted-foreground mt-0.5"
                  minRows={1}
                />
              )}
              {hasItemAdjustment && (
                <div className="mt-1 flex flex-wrap gap-1 print-hidden">
                  {!!item.discount && (
                    <span className="text-[10px] text-red-500 bg-red-50 px-1.5 py-0.5 rounded">
                      -{item.discount}{item.discountType === "fixed" ? currency.symbol : "%"} off
                    </span>
                  )}
                  {!!item.taxRate && (
                    <span className="text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                      +{item.taxRate}% tax
                    </span>
                  )}
                </div>
              )}
              <div className="print-hidden flex flex-wrap items-center gap-2 mt-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity text-xs text-muted-foreground/70">
                {item.details === undefined && (
                  <>
                    <button
                      onClick={() => updateItem(item.id, "details", "")}
                      className="hover:text-foreground/70 flex items-center gap-0.5"
                    >
                      <Plus className="h-3 w-3" /> Details
                    </button>
                    <span>·</span>
                  </>
                )}
                <button onClick={() => duplicateItem(item.id)} className="hover:text-foreground/70 flex items-center gap-0.5">
                  <Copy className="h-3 w-3" /> Duplicate
                </button>
                <span>·</span>
                <button onClick={() => savePresetFromItem(item)} className="hover:text-foreground/70 flex items-center gap-0.5">
                  <Bookmark className="h-3 w-3" /> Save preset
                </button>
                <span>·</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="hover:text-foreground/70 flex items-center gap-0.5">
                      <Settings2 className="h-3 w-3" /> Line options
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="start">
                    <div className="space-y-3 text-sm">
                      <div>
                        <label className="text-xs font-medium text-foreground/70 mb-1 block">Unit type</label>
                        <Select
                          value={item.unit || "units"}
                          onValueChange={(v) => updateItem(item.id, "unit", v as ItemUnit)}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {itemUnits.map((u) => (
                              <SelectItem key={u.id} value={u.id} className="text-xs">
                                {u.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-medium text-foreground/70 mb-1 block">Discount</label>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={item.discount ?? 0}
                              onChange={(e) => updateItem(item.id, "discount", parseFloat(e.target.value) || 0)}
                              className="w-full h-8 px-2 text-xs border rounded"
                              min={0}
                            />
                            <Select
                              value={item.discountType || "percentage"}
                              onValueChange={(v) => updateItem(item.id, "discountType", v as DiscountType)}
                            >
                              <SelectTrigger className="w-12 h-8 text-xs px-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">%</SelectItem>
                                <SelectItem value="fixed">{currency.symbol}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-foreground/70 mb-1 block">Tax %</label>
                          <input
                            type="number"
                            value={item.taxRate ?? 0}
                            onChange={(e) => updateItem(item.id, "taxRate", parseFloat(e.target.value) || 0)}
                            className="w-full h-8 px-2 text-xs border rounded"
                            min={0}
                          />
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground/70">
                        Line totals override invoice-wide discount/tax for this row.
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </td>
        <td className="py-3 px-2">
          <div className="flex items-center justify-end gap-1">
            <InlineInput
              value={item.unit === "fixed" ? "" : String(item.quantity)}
              onChange={(v) => updateItem(item.id, "quantity", parseFloat(v) || 0)}
              placeholder={item.unit === "fixed" ? "—" : "1"}
              type="number"
              step={isHourly ? "0.25" : "1"}
              className="text-right text-sm w-14"
            />
            {unitMeta?.suffix && item.unit !== "fixed" && (
              <span className="text-[10px] text-muted-foreground/70 shrink-0 w-6">{unitMeta.suffix.trim()}</span>
            )}
          </div>
        </td>
        <td className="py-3 px-2">
          <div className="flex items-center justify-end gap-0.5">
            <span className="text-muted-foreground/70 text-sm">{currency.symbol}</span>
            <InlineInput
              value={String(item.rate)}
              onChange={(v) => updateItem(item.id, "rate", parseFloat(v) || 0)}
              placeholder="0"
              type="number"
              step="0.01"
              className="text-right text-sm w-20"
            />
          </div>
        </td>
        <td className="py-3 px-4 text-right text-sm">
          <div className="font-medium">{formatMoney(lineNet)}</div>
          {hasItemAdjustment && item.quantity * item.rate !== lineNet && (
            <div className="text-[10px] text-muted-foreground/70 line-through print-hidden">
              {formatMoney(item.quantity * item.rate)}
            </div>
          )}
        </td>
      </motion.tr>
    )
  }

  const renderItemsTable = () => {
    const isRounded = invoice.layout === "modern" || invoice.layout === "split"
    const showHoursColLabel = invoice.items.some(
      (i) => i.unit === "hours" || i.unit === "days"
    )

    return (
      <div className="mb-6 -mx-2 sm:mx-0 overflow-x-auto">
        <table className="w-full min-w-[520px]">
          <thead>
            <tr style={{ background: colors.tableBg, borderRadius: isRounded ? "8px" : "0" }}>
              <th className={cn("text-left py-3 px-3 sm:px-4 text-[11px] font-semibold uppercase tracking-wide", isRounded && "rounded-l-lg")} style={{ color: colors.tableText }}>Description</th>
              <th className="text-right py-3 px-2 sm:px-4 text-[11px] font-semibold uppercase tracking-wide w-20 sm:w-24" style={{ color: colors.tableText }}>
                {showHoursColLabel ? "Hrs/Qty" : "Qty"}
              </th>
              <th className="text-right py-3 px-2 sm:px-4 text-[11px] font-semibold uppercase tracking-wide w-24 sm:w-28" style={{ color: colors.tableText }}>Rate</th>
              <th className={cn("text-right py-3 px-3 sm:px-4 text-[11px] font-semibold uppercase tracking-wide w-24 sm:w-32", isRounded && "rounded-r-lg")} style={{ color: colors.tableText }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, idx) => renderItemRow(item, idx))}
          </tbody>
        </table>
        <div className="print-hidden flex flex-wrap items-center gap-3 mt-3 text-sm">
          <button
            onClick={() => addItem()}
            className="font-medium hover:underline transition-colors flex items-center gap-1"
            style={{ color: colors.accent }}
          >
            <Plus className="h-3.5 w-3.5" /> Add line item
          </button>
          {presets.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <button className="text-muted-foreground hover:text-foreground/80 flex items-center gap-1 text-xs">
                  <Bookmark className="h-3 w-3" /> From presets ({presets.length})
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-2" align="start">
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  {presets.map((p) => (
                    <div
                      key={p.id}
                      className="group flex items-center justify-between gap-2 p-2 rounded hover:bg-muted/60 cursor-pointer"
                      onClick={() => addItem(p)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{p.description}</div>
                        <div className="text-[10px] text-muted-foreground/70">
                          {currency.symbol}{p.rate} /{" "}
                          {itemUnits.find((u) => u.id === p.unit)?.label.toLowerCase() || "unit"}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          removePreset(p.id)
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    )
  }

  const renderTaxRows = () => {
    if (invoice.taxes.length === 0) {
      return (
        <div className="flex justify-between items-center py-2 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-foreground/70">Tax</span>
            <InlineInput
              value={String(invoice.taxRate)}
              onChange={(v) => updateField("taxRate", parseFloat(v) || 0)}
              placeholder="0"
              type="number"
              className="w-12 text-right text-xs"
            />
            <span className="text-muted-foreground/70 text-xs">%</span>
            <button
              onClick={addTaxLine}
              className="print-hidden text-[10px] text-muted-foreground/70 hover:text-foreground/70 ml-1"
              title="Add another tax line"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
          <span>{formatMoney(globalTaxAmount)}</span>
        </div>
      )
    }

    return (
      <>
        {invoice.taxes.map((tax) => (
          <div key={tax.id} className="flex justify-between items-center py-2 text-sm">
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <button
                onClick={() => removeTaxLine(tax.id)}
                className="print-hidden text-red-400 hover:text-red-600 shrink-0"
                title="Remove tax"
              >
                <X className="h-3 w-3" />
              </button>
              <InlineInput
                value={tax.name}
                onChange={(v) => updateTaxLine(tax.id, { name: v })}
                placeholder="Tax"
                className="text-foreground/70 text-xs w-20"
              />
              <InlineInput
                value={String(tax.rate)}
                onChange={(v) => updateTaxLine(tax.id, { rate: parseFloat(v) || 0 })}
                placeholder="0"
                type="number"
                className="w-12 text-right text-xs"
              />
              <span className="text-muted-foreground/70 text-xs">%</span>
              <button
                onClick={() => updateTaxLine(tax.id, { compound: !tax.compound })}
                className={cn(
                  "print-hidden text-[10px] px-1 rounded ml-1",
                  tax.compound ? "bg-primary text-primary-foreground" : "text-muted-foreground/70 hover:text-foreground/70"
                )}
                title="Compound: tax applies on top of previous taxes"
              >
                C
              </button>
            </div>
            <span>{formatMoney((globalTaxAmount * tax.rate) / invoice.taxes.reduce((s, t) => s + t.rate, 0) || 0)}</span>
          </div>
        ))}
        <div className="print-hidden">
          <button
            onClick={addTaxLine}
            className="text-xs hover:underline"
            style={{ color: colors.accent }}
          >
            + Add tax line
          </button>
        </div>
      </>
    )
  }

  const renderTotals = () => {
    return (
      <>
      {overdueDays > 0 && (
        <div className="print-hidden mb-3 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between p-3 rounded-lg border border-destructive/30 bg-destructive/5 text-sm">
          <div className="text-destructive">
            <span className="font-semibold">{overdueDays} day{overdueDays === 1 ? "" : "s"} overdue.</span>{" "}
            {invoice.lateFeeRate > 0 ? (
              <>Suggested late fee: <span className="font-semibold">{formatMoney(lateFee)}</span></>
            ) : (
              <>Set a late-fee rate to compute the surcharge.</>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-xs">
              <span className="text-muted-foreground">Rate</span>
              <input
                type="number"
                min={0}
                step={0.1}
                value={invoice.lateFeeRate}
                onChange={(e) => updateField("lateFeeRate", Number.parseFloat(e.target.value) || 0)}
                className="w-14 px-1.5 py-0.5 text-right rounded border border-input bg-background text-foreground"
              />
              <span className="text-muted-foreground">%/mo</span>
            </div>
            {lateFee > 0 && (
              <Button size="sm" variant="outline" className="h-8" onClick={addLateFeeAsLineItem}>
                Add as line item
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="totals flex flex-col sm:flex-row justify-between gap-6 mb-6">
        <div className="flex-1 max-w-sm space-y-3">
          <div>
            <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Notes</h4>
            <InlineTextarea value={invoice.notes} onChange={(v) => updateField("notes", v)} placeholder="Payment instructions, thank you note..." className="text-xs" minRows={2} />
          </div>
          {invoice.taxInclusive && (
            <div className="text-[10px] text-muted-foreground italic print-hidden">
              Tax-inclusive: prices already include tax
            </div>
          )}
        </div>
        <div className="totals-box w-full sm:w-80">
          <div className="flex justify-between py-2 text-sm"><span className="text-foreground/70">Subtotal</span><span className="font-medium">{formatMoney(subtotal)}</span></div>
          {itemDiscountTotal > 0 && (
            <div className="flex justify-between py-2 text-sm">
              <span className="text-foreground/70">Line discounts</span>
              <span className="text-red-500">-{formatMoney(itemDiscountTotal)}</span>
            </div>
          )}
          {itemTaxTotal > 0 && invoice.showPerItemTax && (
            <div className="flex justify-between py-2 text-sm">
              <span className="text-foreground/70">Line taxes</span>
              <span>{formatMoney(itemTaxTotal)}</span>
            </div>
          )}
          <AnimatePresence>
            {invoice.showDiscount && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="flex justify-between items-center py-2 text-sm">
                <div className="flex items-center gap-1">
                  <button onClick={() => updateField("showDiscount", false)} className="print-hidden text-red-400"><X className="h-3 w-3" /></button>
                  <span className="text-foreground/70">Discount</span>
                  <InlineInput value={String(invoice.discountRate)} onChange={(v) => updateField("discountRate", parseFloat(v) || 0)} placeholder="0" type="number" className="w-12 text-right text-xs" />
                  <Select
                    value={invoice.discountType}
                    onValueChange={(v) => updateField("discountType", v as DiscountType)}
                  >
                    <SelectTrigger className="w-12 h-6 text-xs px-1 print-hidden">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">%</SelectItem>
                      <SelectItem value="fixed">{currency.symbol}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-red-500">-{formatMoney(globalDiscountAmount)}</span>
              </motion.div>
            )}
          </AnimatePresence>
          {renderTaxRows()}
          <AnimatePresence>
            {invoice.showShipping && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="flex justify-between items-center py-2 text-sm">
                <div className="flex items-center gap-1">
                  <button onClick={() => updateField("showShipping", false)} className="print-hidden text-red-400"><X className="h-3 w-3" /></button>
                  <span className="text-foreground/70">Shipping</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <span className="text-muted-foreground/70">{currency.symbol}</span>
                  <InlineInput value={String(invoice.shipping)} onChange={(v) => updateField("shipping", parseFloat(v) || 0)} placeholder="0" type="number" className="w-20 text-right text-sm" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="print-hidden flex flex-wrap gap-3 py-2 text-xs">
            {!invoice.showDiscount && <button onClick={() => updateField("showDiscount", true)} className="hover:underline" style={{ color: colors.accent }}>+ Discount</button>}
            {!invoice.showShipping && <button onClick={() => updateField("showShipping", true)} className="hover:underline" style={{ color: colors.accent }}>+ Shipping</button>}
          </div>
          <div className="flex justify-between py-2 text-sm border-t border-border">
            <span className="text-foreground/70">Total</span>
            <span className="font-semibold">{formatMoney(total)}</span>
          </div>
          <div className="flex justify-between items-center py-2 text-sm">
            <span className="text-foreground/70">Amount Paid</span>
            <div className="flex items-center gap-0.5">
              <span className="text-muted-foreground/70">{currency.symbol}</span>
              <InlineInput value={String(invoice.amountPaid)} onChange={(v) => updateField("amountPaid", parseFloat(v) || 0)} placeholder="0" type="number" className="w-24 text-right text-sm" />
            </div>
          </div>
          <div className="flex justify-between items-center py-3 px-4 mt-2 rounded-lg" style={{ background: `${colors.accent}10`, borderLeft: `3px solid ${colors.accent}` }}>
            <span className="font-semibold text-sm" style={{ color: colors.accent }}>
              {invoice.documentType === "quote" ? "Quote Total" : invoice.documentType === "receipt" ? "Total Paid" : "Balance Due"}
            </span>
            <span className="font-bold text-xl" style={{ color: colors.accent }}>
              {formatMoney(invoice.documentType === "receipt" ? total : balanceDue)}
            </span>
          </div>
        </div>
      </div>
      </>
    )
  }

  const renderFooter = () => (
    <div className="footer border-t border-border/60 pt-5 mt-4">
      <button onClick={() => setShowAdvanced(!showAdvanced)} className="print-hidden flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground/80 mb-4 transition-colors">
        {showAdvanced ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {showAdvanced ? "Hide" : "Show"} Terms & Payment Info
      </button>
      <AnimatePresence>
        {showAdvanced && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Terms & Conditions</h4>
              <InlineTextarea value={invoice.terms} onChange={(v) => updateField("terms", v)} placeholder="Late fees, payment methods, delivery schedule..." className="text-xs" minRows={2} />
            </div>
            <div>
              <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Payment Information</h4>
              <InlineTextarea value={invoice.paymentInfo} onChange={(v) => updateField("paymentInfo", v)} placeholder="Bank: Name&#10;Account: XXXX&#10;SWIFT/IBAN: XXXX&#10;PayPal: email@example.com" className="text-xs" minRows={2} />
              <div className="mt-3">
                <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Payment link (for QR code)</h4>
                <InlineInput
                  value={invoice.paymentLink}
                  onChange={(v) => updateField("paymentLink", v)}
                  placeholder="https://buy.stripe.com/... or paypal.me/..."
                  className="text-xs w-full"
                />
                <p className="text-[10px] text-muted-foreground/70 mt-1 print-hidden">
                  When set, a QR code is rendered next to the payment info so the client can pay from their phone.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {(invoice.terms || invoice.paymentInfo || visibleQrDataUrl) && (
        <div className="hidden print:grid grid-cols-[1fr_auto] gap-4">
          <div className="grid grid-cols-2 gap-4">
            {invoice.terms && <div><h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Terms & Conditions</h4><p className="text-xs text-foreground/70 whitespace-pre-line">{invoice.terms}</p></div>}
            {invoice.paymentInfo && <div><h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Payment Information</h4><p className="text-xs text-foreground/70 whitespace-pre-line">{invoice.paymentInfo}</p></div>}
          </div>
          {visibleQrDataUrl && (
            <div className="text-center">
              <Image src={visibleQrDataUrl} alt="Payment QR code" width={80} height={80} unoptimized className="w-20 h-20" />
              <div className="text-[8px] uppercase tracking-wider text-muted-foreground mt-1">Scan to pay</div>
            </div>
          )}
        </div>
      )}
      {visibleQrDataUrl && (
        <div className="print-hidden mt-4 flex items-center gap-3 p-3 rounded-lg border border-border/60">
          <Image src={visibleQrDataUrl} alt="Payment QR code" width={64} height={64} unoptimized className="w-16 h-16 shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-semibold">Pay via QR code</div>
            <div className="text-[10px] text-muted-foreground truncate">{invoice.paymentLink}</div>
          </div>
        </div>
      )}
    </div>
  )

  const renderSidebarLayout = () => (
    <div className="flex">
      <div className="w-56 shrink-0 p-6 text-white" style={{ background: colors.accent }}>
        {invoice.logo ? (
          <div className="relative group mb-6">
            <Image src={invoice.logo} alt="Company logo" width={180} height={60} unoptimized className="max-w-full max-h-[60px] object-contain" />
            <button onClick={() => updateField("logo", null)} className="print-hidden absolute -top-1 -right-1 bg-white text-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100">
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <button onClick={() => logoInputRef.current?.click()} className="print-hidden mb-6 w-full h-[50px] border-2 border-dashed border-white/30 rounded flex items-center justify-center text-white/50 hover:border-white/50 text-xs">
            <ImageIcon className="h-4 w-4 mr-1" /> Logo
          </button>
        )}
        <h1 className="text-2xl font-bold mb-3">{docType.title}</h1>
        {invoice.status !== "none" && (
          <div className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-white/15 text-white mb-4">
            {statusConfig.label}
          </div>
        )}
        <div className="space-y-3 text-sm">
          <div><div className="text-white/60 text-xs uppercase">Number</div><InlineInput value={invoice.invoiceNumber} onChange={(v) => updateField("invoiceNumber", v)} placeholder="INV-0001" variant="light" className="w-full" /></div>
          <div><div className="text-white/60 text-xs uppercase">Date</div><InlineInput value={invoice.issueDate} onChange={updateIssueDate} type="date" placeholder="" variant="light" className="w-full" /></div>
          <div><div className="text-white/60 text-xs uppercase">Due</div><InlineInput value={invoice.dueDate} onChange={(v) => updateField("dueDate", v)} type="date" placeholder="" variant="light" className="w-full" /></div>
          <div><div className="text-white/60 text-xs uppercase">Terms</div><InlineInput value={invoice.paymentTerms} onChange={updatePaymentTerms} placeholder="Net 30" variant="light" className="w-full" /></div>
          {invoice.poNumber && (
            <div><div className="text-white/60 text-xs uppercase">PO #</div><InlineInput value={invoice.poNumber} onChange={(v) => updateField("poNumber", v)} placeholder="" variant="light" className="w-full" /></div>
          )}
        </div>
        <div className="mt-8 pt-6 border-t border-white/20">
          <div className="text-white/60 text-xs uppercase mb-1">
            {invoice.documentType === "receipt" ? "Total Paid" : "Balance Due"}
          </div>
          <div className="text-2xl font-bold">
            {formatMoney(invoice.documentType === "receipt" ? total : balanceDue)}
          </div>
        </div>
      </div>
      <div className="flex-1 p-8">
        {renderParties()}
        {renderItemsTable()}
        {renderTotals()}
        {renderFooter()}
      </div>
    </div>
  )

  return (
    <div className="flex flex-col xl:flex-row gap-4">
      <div className="flex-1 min-w-0">
        <div className="print-hidden mb-5 flex flex-wrap items-center gap-2">
          <Select value={invoice.documentType} onValueChange={(v) => updateField("documentType", v as DocumentType)}>
            <SelectTrigger className="w-28 sm:w-32 h-10 text-sm shadow-sm">
              <SelectValue>{docType.label}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {documentTypes.map((d) => (
                <SelectItem key={d.id} value={d.id} className="py-2">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{d.label}</span>
                    <span className="text-xs text-muted-foreground">{d.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={invoice.status} onValueChange={(v) => updateField("status", v as InvoiceStatus)}>
            <SelectTrigger className="w-28 sm:w-32 h-10 text-sm shadow-sm">
              <div className="flex items-center gap-1.5">
                {invoice.status !== "none" && (
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: statusConfig.color }}
                  />
                )}
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {statuses.map((s) => (
                <SelectItem key={s.id} value={s.id} className="text-sm">
                  <div className="flex items-center gap-2">
                    {s.id !== "none" && (
                      <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                    )}
                    {s.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={invoice.layout} onValueChange={(v) => updateField("layout", v as LayoutId)}>
            <SelectTrigger className="hidden sm:flex w-32 lg:w-36 h-10 text-sm shadow-sm">
              <SelectValue>{layouts.find((l) => l.id === invoice.layout)?.name || "Layout"}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {layouts.map((l) => (
                <SelectItem key={l.id} value={l.id} className="py-2">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{l.name}</span>
                    <span className="text-xs text-muted-foreground">{l.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={invoice.colorScheme} onValueChange={(v) => updateField("colorScheme", v as ColorSchemeId)}>
            <SelectTrigger
              aria-label="Color scheme"
              className="hidden sm:flex w-32 h-10 text-sm shadow-sm md:w-32 lg:w-32 [&_>span:has(svg)]:hidden"
            >
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colors.accent }} />
                <span className="hidden md:inline"><SelectValue placeholder="Color" /></span>
              </div>
            </SelectTrigger>
            <SelectContent>
              {colorSchemes.map((c) => (
                <SelectItem key={c.id} value={c.id} className="py-2">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.accent }} />
                    <span className="text-sm">{c.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={invoice.currency} onValueChange={(v) => updateField("currency", v)}>
            <SelectTrigger className="w-20 sm:w-24 h-10 text-sm shadow-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c.code} value={c.code} className="text-sm">
                  <span className="font-medium">{c.symbol}</span> {c.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 h-10 px-2 sm:px-3 text-sm shadow-sm">
                <MoreHorizontal className="h-4 w-4 sm:hidden" />
                <Settings2 className="h-4 w-4 hidden sm:inline" />
                <span className="hidden sm:inline">Options</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="start">
              <div className="space-y-3">
                <h4 className="text-sm font-semibold">Invoice options</h4>
                <Toggle
                  label="Tax-inclusive prices"
                  description="Item prices already include tax"
                  checked={invoice.taxInclusive}
                  onChange={(v) => updateField("taxInclusive", v)}
                />
                <Toggle
                  label="Per-line tax rates"
                  description="Set tax per item via line options"
                  checked={invoice.showPerItemTax}
                  onChange={(v) => updateField("showPerItemTax", v)}
                />
                <div className="pt-2 border-t">
                  <label className="text-xs font-medium text-foreground/70 mb-1.5 block">PO Number</label>
                  <input
                    type="text"
                    value={invoice.poNumber}
                    onChange={(e) => updateField("poNumber", e.target.value)}
                    placeholder="Optional purchase order #"
                    className="w-full h-8 px-2 text-sm border rounded"
                  />
                </div>
                <div className="sm:hidden">
                  <label className="text-xs font-medium text-foreground/70 mb-1.5 block">Layout</label>
                  <Select value={invoice.layout} onValueChange={(v) => updateField("layout", v as LayoutId)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {layouts.map((l) => (
                        <SelectItem key={l.id} value={l.id} className="text-sm">
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground/70 mb-1.5 block">Default unit for new items</label>
                  <Select
                    value={invoice.defaultItemUnit}
                    onValueChange={(v) => updateField("defaultItemUnit", v as ItemUnit)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {itemUnits.map((u) => (
                        <SelectItem key={u.id} value={u.id} className="text-sm">
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="pt-3 border-t space-y-1">
                  <button
                    onClick={loadExample}
                    className="w-full text-left text-sm flex items-center gap-2 p-2 rounded hover:bg-muted/60"
                  >
                    <Sparkles className="h-4 w-4 text-muted-foreground" /> Load example invoice
                  </button>
                  <button
                    onClick={exportJson}
                    className="w-full text-left text-sm flex items-center gap-2 p-2 rounded hover:bg-muted/60"
                  >
                    <FileJson className="h-4 w-4 text-muted-foreground" /> Export as JSON
                  </button>
                  <button
                    onClick={exportCsv}
                    className="w-full text-left text-sm flex items-center gap-2 p-2 rounded hover:bg-muted/60"
                  >
                    <FileSpreadsheet className="h-4 w-4 text-muted-foreground" /> Export items as CSV
                  </button>
                  <button
                    onClick={() => jsonImportRef.current?.click()}
                    className="w-full text-left text-sm flex items-center gap-2 p-2 rounded hover:bg-muted/60"
                  >
                    <Upload className="h-4 w-4 text-muted-foreground" /> Import from JSON
                  </button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {history.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 h-10 px-2 sm:px-3 text-sm shadow-sm">
                  <History className="h-4 w-4" />
                  <span className="hidden md:inline">History</span>
                  <span className="text-xs text-muted-foreground/70">({history.length})</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-96 p-2" align="end">
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {history.map((h) => (
                    <div
                      key={h.meta.id}
                      className="group flex items-center justify-between gap-2 p-2 rounded hover:bg-muted/60 cursor-pointer"
                      onClick={() => loadFromHistory(h.data)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{h.meta.invoiceNumber}</span>
                          {h.meta.status !== "none" && (
                            <span
                              className="text-[9px] px-1.5 py-0.5 rounded uppercase"
                              style={{
                                background: statuses.find((s) => s.id === h.meta.status)?.bg,
                                color: statuses.find((s) => s.id === h.meta.status)?.color,
                              }}
                            >
                              {h.meta.status}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground/70 truncate">
                          {h.meta.billTo} · {h.meta.issueDate} ·{" "}
                          {formatMoneyLocale(h.meta.total, h.meta.currency)}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteFromHistory(h.meta.id)
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 p-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          <div className="flex-1" />

          <Button
            variant="outline"
            size="sm"
            onClick={duplicateInvoice}
            className="gap-2 h-10 px-2 sm:px-3 text-sm shadow-sm"
            title="Duplicate as new invoice"
          >
            <Copy className="h-4 w-4" />
            <span className="hidden lg:inline">Duplicate</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={printInvoice}
            className="gap-2 h-10 px-2 sm:px-3 text-sm shadow-sm"
            title="Print invoice"
          >
            <Printer className="h-4 w-4" />
            <span className="hidden lg:inline">Print</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={resetInvoice}
            className="gap-2 h-10 px-2 sm:px-3 text-sm shadow-sm"
            title="Save current & start new"
          >
            <FilePlus2 className="h-4 w-4" />
            <span className="hidden md:inline">New</span>
          </Button>

          <Button
            onClick={downloadPDF}
            disabled={isGeneratingPDF}
            size="sm"
            className="gap-2 h-10 px-3 sm:px-5 text-sm font-medium shadow-md hover:shadow-lg transition-shadow disabled:opacity-70 text-white"
            style={{ background: colors.accent }}
          >
            {isGeneratingPDF ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="hidden sm:inline">
              {isGeneratingPDF ? "Generating..." : "Download PDF"}
            </span>
            <span className="sm:hidden">{isGeneratingPDF ? "..." : "PDF"}</span>
          </Button>
          <input
            ref={jsonImportRef}
            type="file"
            accept="application/json,.json"
            onChange={importJson}
            className="hidden"
          />
        </div>

        {(!invoice.from && !invoice.billTo && invoice.items.length === 1 && !invoice.items[0].description) && (
          <div className="print-hidden mb-4 flex items-center justify-between gap-3 p-3 rounded-lg bg-primary/5 border border-primary/15 text-sm">
            <div className="flex items-center gap-2 text-primary">
              <Sparkles className="h-4 w-4 shrink-0" />
              <span>First time? Try a pre-filled example to see all features.</span>
            </div>
            <Button size="sm" variant="outline" onClick={loadExample} className="shrink-0 h-8">
              Load example
            </Button>
          </div>
        )}

        <Card className="overflow-hidden shadow-xl border border-border/40 ring-1 ring-black/5 dark:ring-white/10 relative">
          {invoice.status === "paid" && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 print-hidden">
              <div
                className="text-[8rem] font-black opacity-[0.06] dark:opacity-[0.10] print:opacity-[0.06] rotate-[-20deg] tracking-widest select-none"
                style={{ color: "#047857" }}
              >
                PAID
              </div>
            </div>
          )}
          {invoice.status === "overdue" && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 print-hidden">
              <div
                className="text-[7rem] font-black opacity-[0.06] dark:opacity-[0.11] print:opacity-[0.06] rotate-[-20deg] tracking-widest select-none"
                style={{ color: "#b91c1c" }}
              >
                OVERDUE
              </div>
            </div>
          )}
          {invoice.status === "draft" && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 print-hidden">
              <div className="text-[8rem] font-black opacity-[0.08] dark:opacity-[0.14] rotate-[-20deg] tracking-widest select-none text-muted-foreground print:opacity-[0.05]">
                DRAFT
              </div>
            </div>
          )}
          <div ref={printRef} className={cn("invoice-doc bg-card text-card-foreground relative print:bg-white print:text-gray-800", invoice.layout !== "sidebar" && "p-8 sm:p-10")}>
            <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            {invoice.layout === "sidebar" ? (
              renderSidebarLayout()
            ) : (
              <>
                {renderHeader()}
                {renderParties()}
                {renderItemsTable()}
                {renderTotals()}
                {renderFooter()}
              </>
            )}
          </div>
        </Card>

        <div className="sm:hidden h-20" aria-hidden />
        <div
          className="print-hidden sm:hidden fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/80 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center justify-between gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
        >
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {invoice.documentType === "receipt" ? "Total Paid" : "Balance Due"}
            </div>
            <div className="text-lg font-bold truncate" style={{ color: colors.accent }}>
              {formatMoney(invoice.documentType === "receipt" ? total : balanceDue)}
            </div>
          </div>
          <Button
            onClick={downloadPDF}
            disabled={isGeneratingPDF}
            className="gap-2 h-11 px-5 text-sm font-semibold text-white shrink-0"
            style={{ background: colors.accent }}
          >
            {isGeneratingPDF ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isGeneratingPDF ? "..." : "PDF"}
          </Button>
        </div>
      </div>
    </div>
  )
}

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className="w-full flex items-start justify-between gap-3 text-left p-2 rounded hover:bg-muted/60 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        {description && <div className="text-xs text-muted-foreground/70">{description}</div>}
      </div>
      <span
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors mt-0.5",
          checked ? "bg-foreground" : "bg-muted"
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-4" : "translate-x-0.5"
          )}
        />
      </span>
    </button>
  )
}

function PaymentTermsField({
  value,
  onChange,
  width = "w-28",
}: {
  value: string
  onChange: (v: string) => void
  width?: string
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "text-right text-sm border-b border-transparent hover:border-input px-1.5 py-1 rounded-sm hover:bg-muted/60 transition-colors text-foreground",
            width
          )}
        >
          {value || "Net 30"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="end">
        <div className="space-y-0.5">
          {paymentTermsPresets.map((p) => (
            <button
              key={p.label}
              onClick={() => onChange(p.label)}
              className={cn(
                "w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted/60 transition-colors",
                value === p.label && "bg-muted font-medium"
              )}
            >
              {p.label}
              {p.days !== null && p.days > 0 && (
                <span className="text-[10px] text-muted-foreground/70 ml-2">{p.days}d</span>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
